import React, { useCallback, useContext, useEffect, useMemo } from 'react';
import { OrganizationsContext } from '../OrganizationContextProvider';
import { useParams, generatePath } from 'react-router-dom';
import { useNavigate } from 'src/utils/useNavigate';
import useOrganizationBanks from 'src/dataHooks/useOrganizationBanks';
import { Table } from '@1c/ui';
import PageSpin from 'src/components/PageSpin';
import Button from 'src/components/Button';
import Link from 'src/components/Link';
import Empty from './Empty';
import DeleteBankLink from './DeleteBankLink';
import LeaveOrganizationLink from '../LeaveOrganizationLink';
import routes from 'src/constants/routes';
import styles from './styles.module.scss';

const getColumns = (currentOrganization: any, handleBanksChange: () => void) => [
  {
    Header: 'Название',
    accessor: 'name',
    style: { textAlign: 'center' },
    width: '100%',
  },
  {
    Header: 'Расчётный счёт',
    accessor: 'account',
    style: { textAlign: 'center' },
    width: '100%',
  },
  {
    Header: 'Действие',
    style: { textAlign: 'center' },
    width: '100%',
    Cell: ({ original }: { original: any }) => {
      return (
        <>
          <div>
            <Link
              to={generatePath(routes.merchants, {
                organizationUeid: original.organizationUeid,
                bankUeid: original.ueid,
              })}
              state={{ organization: currentOrganization, bank: original }}
            >
              Список ТСП
            </Link>
          </div>
          {original.rbsUrl && (
            <div>
              <a href={original.rbsUrl} target="_blank">
                Проверить настройки по СБП в банке
              </a>
            </div>
          )}
          <div>
            <DeleteBankLink
              organizationUeid={original.organizationUeid}
              organizationName={currentOrganization.name}
              bankUeid={original.ueid}
              bankName={original.name}
              handleBanksChange={handleBanksChange}
            />
          </div>
        </>
      );
    },
  },
];

const OrganizationBanks: React.FC = () => {
  const { organizationUeid } = useParams();
  const { currentOrganization } = useContext(OrganizationsContext);
  const navigate = useNavigate();

  const {
    isOrganizationBanksLoading,
    organizationBanks,
    fetchOrganizationBanks,
    organizationBanksError,
  } = useOrganizationBanks();

  useEffect(() => {
    fetchOrganizationBanks(organizationUeid || '');
  }, [fetchOrganizationBanks, organizationUeid]);

  const handleBanksChange = useCallback(() => {
    fetchOrganizationBanks(organizationUeid || '');
  }, [fetchOrganizationBanks]);

  const columns = useMemo(() => getColumns(currentOrganization, handleBanksChange), [
    currentOrganization,
  ]);
  
  const handleAddBankClick = () => {
    navigate(generatePath(routes.addBank, { organizationUeid }), {
      state: {
        organization: currentOrganization,
      },
    });
  }

  if (isOrganizationBanksLoading) {
    return <PageSpin />;
  }

  if (!organizationBanks || organizationBanks.length === 0) {
    return <Empty error={!!organizationBanksError}/>;
  }

  return (
    <div>
      <div className={styles['leave-organization-link-wrapper']}>
        <LeaveOrganizationLink />
      </div>
      <Table
        columns={columns}
        data={organizationBanks}
        noDataText="Нет данных"
        sortable={false}
        resizable={false}
        showPagination={false}
        pageSize={organizationBanks.length}
      />
      <Button variant="outlined" onClick={handleAddBankClick} className={styles['add-bank-button']}>
        Добавить банк
      </Button>
    </div>
  );
};

export default OrganizationBanks;
